import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchHistoriqueEntretien } from "../../store/actions/entretienActions"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import Loader from "../basics/Loader"
import {
	STATUTS,
	Pill,
	echeanceDetail,
	echeanceTitre,
	fmtDate,
	fmtKm,
	suivanteTxt,
	tableStyles as ts,
} from "./entretienUtils"

const styles = {
	overlay: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(28, 25, 23, 0.45)",
		display: "flex",
		justifyContent: "flex-end",
		zIndex: 900,
	},
	drawer: {
		width: "min(440px, 100%)",
		height: "100%",
		backgroundColor: COLORS.surface,
		padding: "22px 22px 30px",
		overflowY: "auto",
		display: "flex",
		flexDirection: "column",
		gap: 18,
		boxSizing: "border-box",
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
		color: COLORS.text,
	},
	head: { display: "flex", gap: 10, alignItems: "flex-start" },
	title: { fontSize: 18, fontWeight: 700, margin: 0 },
	lbl: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		color: COLORS.textLight,
		marginBottom: 8,
	},
	close: (hover) => ({
		border: "none",
		background: hover ? COLORS.bg : "none",
		color: hover ? COLORS.text : COLORS.textMuted,
		cursor: "pointer",
		fontSize: 18,
		padding: "2px 8px",
		borderRadius: 6,
		fontFamily: "inherit",
	}),
	kv: {
		display: "grid",
		gridTemplateColumns: "auto 1fr",
		gap: "6px 16px",
		fontSize: 13,
		margin: 0,
	},
	dt: { color: COLORS.textMuted, fontWeight: 500 },
	dd: { margin: 0, fontWeight: 600, fontVariantNumeric: "tabular-nums" },
	tl: { listStyle: "none", margin: 0, padding: 0 },
	tlItem: (last) => ({
		display: "grid",
		gridTemplateColumns: "18px 1fr auto",
		gap: 10,
		alignItems: "start",
		paddingBottom: last ? 0 : 14,
		position: "relative",
	}),
	tlLine: {
		position: "absolute",
		left: 8,
		top: 14,
		bottom: 0,
		width: 2,
		backgroundColor: COLORS.border,
	},
	bullet: (kind) => ({
		width: 12,
		height: 12,
		borderRadius: "50%",
		margin: "3px 0 0 3px",
		boxSizing: "border-box",
		position: "relative",
		zIndex: 1,
		border: `2px ${kind === "next" ? "dashed" : "solid"} ${
			kind === "done"
				? COLORS.text
				: kind === "open"
					? COLORS.warning
					: COLORS.borderStrong
		}`,
		backgroundColor:
			kind === "done"
				? COLORS.text
				: kind === "open"
					? COLORS.warningLight
					: COLORS.surface,
	}),
	tlTitle: { fontWeight: 700, fontSize: 13 },
	tlSub: { fontSize: 12, color: COLORS.textMuted },
	explain: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		backgroundColor: COLORS.bg,
		borderRadius: 10,
		padding: "10px 12px",
		lineHeight: 1.5,
	},
	actions: { display: "flex", gap: 8, flexWrap: "wrap" },
	btn: { ...gs.btnSecondary, fontSize: 13, padding: "8px 16px" },
}

const cycleTitre = (c) =>
	c.date
		? fmtDate(c.date)
		: c.km_cible !== null && c.km_cible !== undefined
			? fmtKm(c.km_cible)
			: "—"

const TimelineItem = ({ kind, last, title, sub, action }) => (
	<li style={styles.tlItem(last)}>
		{!last && <span style={styles.tlLine} />}
		<span style={styles.bullet(kind)} />
		<div>
			<div style={styles.tlTitle}>{title}</div>
			{sub && <div style={styles.tlSub}>{sub}</div>}
		</div>
		{action || <span />}
	</li>
)

const EntretienHistoriqueDrawer = ({
	echeance: e,
	onClose,
	onVoirFiche,
	onCreerFiche,
	onDecaler,
	onEditPlan,
}) => {
	const dispatch = useDispatch()
	const { historique, fetchHistorique } = useSelector(
		(state) => state.entretienInfo,
	)
	const [hoverClose, setHoverClose] = useState(false)

	useEffect(() => {
		dispatch(
			fetchHistoriqueEntretien({
				entretien: e.entretien.id,
				vehicule: e.vehicule.id,
			}),
		)
	}, [dispatch, e.entretien.id, e.vehicule.id])

	useEffect(() => {
		const onKey = (ev) => ev.key === "Escape" && onClose()
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [onClose])

	const st = STATUTS[e.statut] || STATUTS.a_venir
	const passes = historique.filter((c) => c.done)
	const suivante = suivanteTxt(e)
	const items = [
		...passes.map((c) => ({
			key: `c${c.id}`,
			kind: "done",
			title: cycleTitre(c),
			sub: c.fiche ? c.fiche.etat_label : "Fait",
			action: c.fiche && (
				<button
					type="button"
					style={ts.link}
					onClick={() => onVoirFiche(c.fiche.id)}
				>
					Fiche n° {c.fiche.id}
				</button>
			),
		})),
		{
			key: "open",
			kind: "open",
			title: `${echeanceTitre(e)} · échéance en cours`,
			sub: e.fiche ? e.fiche.etat_label : "Pas encore de fiche",
			action: e.fiche ? (
				<button
					type="button"
					style={ts.link}
					onClick={() => onVoirFiche(e.fiche.id)}
				>
					Fiche n° {e.fiche.id}
				</button>
			) : (
				<button
					type="button"
					style={ts.link}
					onClick={() => onCreerFiche(e)}
				>
					Créer la fiche
				</button>
			),
		},
		...(suivante
			? [
					{
						key: "next",
						kind: "next",
						title: suivante,
						sub: "Échéance suivante, créée à la clôture de la fiche",
					},
				]
			: []),
	]

	return (
		<div
			style={styles.overlay}
			onClick={(ev) => ev.target === ev.currentTarget && onClose()}
		>
			<aside style={styles.drawer} role="dialog" aria-label="Historique">
				<div style={styles.head}>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={styles.lbl}>
							{[e.vehicule.label, e.vehicule.sous_label]
								.filter(Boolean)
								.join(" · ")}
						</div>
						<h2 style={styles.title}>{e.entretien.label}</h2>
					</div>
					<button
						type="button"
						aria-label="Fermer"
						style={styles.close(hoverClose)}
						onMouseEnter={() => setHoverClose(true)}
						onMouseLeave={() => setHoverClose(false)}
						onClick={onClose}
					>
						✕
					</button>
				</div>

				<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
					<Pill tone={st}>{st.label}</Pill>
					<span style={ts.sub}>{echeanceDetail(e)}</span>
				</div>

				<dl style={styles.kv}>
					<dt style={styles.dt}>Cycle</dt>
					<dd style={styles.dd}>{e.entretien.cycle_label}</dd>
					<dt style={styles.dt}>Appliqué à</dt>
					<dd style={styles.dd}>{e.entretien.portee_label}</dd>
					{e.entretien.partie_label && (
						<>
							<dt style={styles.dt}>Partie</dt>
							<dd style={styles.dd}>
								{e.entretien.partie_label}
							</dd>
						</>
					)}
					<dt style={styles.dt}>Km actuel</dt>
					<dd style={styles.dd}>{fmtKm(e.vehicule.kms)}</dd>
				</dl>

				<div>
					<div style={styles.lbl}>Historique du cycle</div>
					{fetchHistorique.loading ? (
						<Loader variant="ring" size={24} padding={16} />
					) : (
						<ol style={styles.tl}>
							{items.map((it, i) => (
								<TimelineItem
									key={it.key}
									kind={it.kind}
									last={i === items.length - 1}
									title={it.title}
									sub={it.sub}
									action={it.action}
								/>
							))}
						</ol>
					)}
				</div>

				<div style={styles.explain}>
					{e.entretien.unique ? (
						"Entretien unique : la fiche atelier est créée dès l'enregistrement de l'entretien. Pas d'échéance suivante."
					) : (
						<>
							La fiche atelier se crée à la main (« Créer la fiche
							»). À sa clôture, l'échéance suivante est calculée
							depuis la <b>date prévue</b> + la période, et depuis
							le <b>km relevé à la clôture</b> + l'intervalle.
						</>
					)}
				</div>

				<div style={styles.actions}>
					<button
						type="button"
						style={styles.btn}
						onClick={() => onDecaler(e)}
					>
						Décaler cette échéance
					</button>
					<button
						type="button"
						style={styles.btn}
						onClick={() => onEditPlan(e)}
					>
						Modifier le plan
					</button>
				</div>
			</aside>
		</div>
	)
}

export default EntretienHistoriqueDrawer
